import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";

import {
  UpdateFormDraftRequest,
  UpdateFormDraftResponse,
} from "../types/formDraftApi";

interface PatchFormDraftVariables<
  T = Record<string, unknown>,
> extends UpdateFormDraftRequest<T> {
  draftId: number;
}

type UsePatchFormDraftMutationResponse<
  T = Record<string, unknown>,
  TContext = unknown,
> = UseMutationResult<
  UpdateFormDraftResponse<T>,
  Error,
  PatchFormDraftVariables<T>,
  TContext
>;

/**
 * Hook to update a form draft
 * @param draftId - The ID of the form draft to update
 * @param options - Mutation options (excluding mutationFn) from React Query
 * @returns Mutation object with mutate function and state
 */
function usePatchFormDraftMutation<
  T = Record<string, unknown>,
  TContext = unknown,
>(
  options?: Omit<
    UseMutationOptions<
      UpdateFormDraftResponse<T>,
      Error,
      PatchFormDraftVariables<T>,
      TContext
    >,
    "mutationFn"
  >,
): UsePatchFormDraftMutationResponse<T, TContext> {
  const axiosInstance = useApi();

  return useMutation({
    mutationFn: ({ draftId, ...body }: PatchFormDraftVariables<T>) =>
      axiosInstance
        .patch<
          UpdateFormDraftResponse<T>
        >(`brokerportal/v2/form-drafts/${draftId}`, body)
        .then((res) => res.data),
    ...options,
  });
}

export { usePatchFormDraftMutation, UsePatchFormDraftMutationResponse };
